import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  NzTableFilterFn,
  NzTableFilterList,
  NzTableModule,
  NzTableQueryParams,
  NzTableSortFn,
  NzTableSortOrder,
} from 'ng-zorro-antd/table';
import { NgForOf, NgIf } from '@angular/common';
import { Locker } from '../../api/locker-api.class';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { filter, firstValueFrom, map, Observable } from 'rxjs';
import { StoreService } from '../../store/store.service';
import { toLower } from 'lodash';
import { DateTime } from 'luxon';
import { NzSpinComponent } from 'ng-zorro-antd/spin';

interface ColumnItem {
  id: number;
  name: string;
  sortOrder: NzTableSortOrder | null;
  sortFn: NzTableSortFn<Locker> | null;
  listOfFilter: NzTableFilterList;
  filterFn: NzTableFilterFn<Locker> | null;
}

@Component({
  selector: 'app-locker-list',
  standalone: true,
  imports: [
    NzTableModule,
    NgForOf,
    NzButtonComponent,
    NzIconDirective,
    NzInputDirective,
    NzInputGroupComponent,
    ReactiveFormsModule,
    FormsModule,
    NgIf,
    NzSpinComponent,
  ],
  templateUrl: './locker-list.component.html',
  styleUrl: './locker-list.component.scss',
})
export class LockerListComponent implements OnInit, AfterViewInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  loading = false;
  selectedLocker: number | undefined;
  displayedLockers: Locker[] = [];
  lockers$: Observable<Locker[]>;
  searchValue = '';
  private isRestoringTableState = false;
  listOfColumns: ColumnItem[] = [
    {
      id: 0,
      name: 'Bezeichnung',
      sortOrder: null,
      sortFn: (a: Locker, b: Locker) =>
        a.bezeichnung.localeCompare(b.bezeichnung),
      listOfFilter: [],
      filterFn: null,
    },
    {
      id: 1,
      name: 'Spindart',
      sortOrder: null,
      sortFn: (a: Locker, b: Locker) => a.spindart.localeCompare(b.spindart),
      listOfFilter: [
        { text: 'Schmal', value: 'Schmal (300mm)' },
        { text: 'Breit', value: 'Breit (400mm)' },
        { text: 'Doppelspind', value: 'Doppelspind' },
        { text: 'Kleiderfach', value: 'Kleiderfach' },
        { text: 'Helmfach', value: 'Helmfach' },
        { text: 'Sammelspind', value: 'Sammelspind' },
      ],
      filterFn: (list: string[], item: Locker) =>
        list.some((spindart) => item.spindart === spindart),
    },
    {
      id: 2,
      name: 'Geschlecht',
      sortOrder: null,
      sortFn: (a: Locker, b: Locker) =>
        a.geschlecht.localeCompare(b.geschlecht),
      listOfFilter: [
        { text: 'Männlich', value: 'Männlich' },
        { text: 'Weiblich', value: 'Weiblich' },
        { text: 'Divers', value: 'Divers' },
      ],
      filterFn: (list: string[], item: Locker) =>
        list.some((geschlecht) => item.geschlecht === geschlecht),
    },
    {
      id: 3,
      name: 'Status',
      sortFn: (a: Locker, b: Locker) => a.status.localeCompare(b.status),
      sortOrder: null,
      listOfFilter: [
        { text: 'Frei', value: 'Frei' },
        { text: 'Belegt', value: 'Belegt' },
        { text: 'Gesperrt', value: 'Gesperrt' },
      ],
      filterFn: (list: string[], item: Locker) =>
        list.some((status) => item.status === status),
    },
    {
      id: 4,
      name: 'Standort',
      sortFn: (a: Locker, b: Locker) => a.standort.localeCompare(b.standort),
      sortOrder: null,
      listOfFilter: [
        { text: 'B835', value: 'B835' },
        { text: 'B836', value: 'B836' },
        { text: 'B845', value: 'B845' },
      ],
      filterFn: (list: string[], item: Locker) =>
        list.some((standort) => item.standort === standort),
    },
    {
      id: 5,
      name: 'Ausleihdatum',
      sortFn: (a: Locker, b: Locker) => {
        const dateA = a.schluesselAusgebenAm
          ? DateTime.fromISO(a.schluesselAusgebenAm)
          : null;
        const dateB = b.schluesselAusgebenAm
          ? DateTime.fromISO(b.schluesselAusgebenAm)
          : null;

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateA.toMillis() - dateB.toMillis();
      },
      sortOrder: null,
      listOfFilter: [],
      filterFn: null,
    },
  ];

  tableState$ = this.store.select((state) => state.tables.lockerListTable);

  constructor(
    private router: Router,
    private store: StoreService,
  ) {
    this.lockers$ = this.store
      .select((state) => state.lockers.activeLockers)
      .pipe(
        filter((lockers) => !!lockers),
        map((lockers) => Locker.fromResultArray(lockers)),
      );
  }

  async ngOnInit() {
    this.isRestoringTableState = true;
    this.loading = true;

    const tableState = await firstValueFrom(this.tableState$);

    if (tableState) {
      const {
        tables: { applyFiltersToColumns, applySortToColumns },
      } = this.store.getActions();

      applyFiltersToColumns(tableState, this.listOfColumns);
      applySortToColumns(tableState, this.listOfColumns);
    }

    // 2) Daten laden
    const {
      lockers: { setActiveLockers },
    } = this.store.getActions();
    await setActiveLockers();
    this.displayedLockers = await firstValueFrom(this.lockers$);

    // 3) Guards & Loading freigeben
    this.isRestoringTableState = false;
    this.loading = false;
  }

  onTableQueryParamsChange(params: NzTableQueryParams) {
    // Guard: verhindern, dass TableQueryParams vor Init den Store überschreiben
    if (this.isRestoringTableState) {
      return;
    }

    const {
      tables: { buildTableStateFromQueryParams },
    } = this.store.getActions();

    const tableState = buildTableStateFromQueryParams(
      params,
      this.listOfColumns,
    );

    const {
      tables: { setLockerTableState },
    } = this.store.getActions();
    setLockerTableState(tableState);
  }

  ngAfterViewInit() {
    this.searchInput.nativeElement.focus();
  }

  onLockerClick(data: Locker) {
    this.selectedLocker = data.id;
    this.router.navigate(['lockers', this.selectedLocker]);
  }

  async onSearch() {
    const lockers = await firstValueFrom(this.lockers$);

    this.displayedLockers =
      this.searchValue.length > 0
        ? lockers.filter((locker) =>
            toLower(locker.bezeichnung).includes(toLower(this.searchValue)),
          )
        : lockers;
  }

  getStatus(data: Locker) {
    if (data.status === 'Belegt') {
      return data.zugewiesenAn?.vorname + ' ' + data.zugewiesenAn?.name;
    }
    return data.status;
  }

  geFormattedDate(data: Locker) {
    if (data.status === 'Belegt') {
      if (data.schluesselAusgebenAm) {
        return DateTime.fromISO(data.schluesselAusgebenAm).toFormat('DD', {
          locale: 'de',
        });
      } else return '-';
    }
    return '-';
  }
}
